import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Mail, Pencil, Shield, Trash2, User, UserCheck, UserX } from 'lucide-react';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import LoginHistoryTab from '@ui-admin/components/LoginHistoryTab';
import type { LoginHistoryResult } from '@ui-admin/components/LoginHistoryTab';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import EditSystemUserModal from './EditSystemUserModal';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import { getSystemUser, getSystemUserLoginHistory, updateSystemUserStatus } from '../services/systemUserService';

interface SystemUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  title?: string;
  position?: string;
  status: string;
  systemRole?: { roleId: string; roleName: string } | null;
  createdAt?: string;
  lastLoginAt?: string;
  updatedAt?: string;
  entraUserId?: string;
  identities?: any[];
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700' },
  suspended: { label: 'Suspended', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  invited: { label: 'Invited', bg: 'bg-blue-100', text: 'text-blue-700' },
  deleted: { label: 'Deleted', bg: 'bg-red-100', text: 'text-red-700' },
};

export default function SystemUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { setBreadcrumbLabel } = useBreadcrumbs();
  const [user, setUser] = useState<SystemUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const nextUser = await getSystemUser(id);
      if (!nextUser) {
        setUser(null);
        return;
      }
      setUser(nextUser as SystemUser);
      const fullName = [nextUser.firstName, nextUser.lastName].filter(Boolean).join(' ') || nextUser.email;
      setBreadcrumbLabel(`/users/${id}`, fullName);
    } finally {
      setLoading(false);
    }
  }, [id, setBreadcrumbLabel]);

  useEffect(() => {
    loadData().catch(() => {
      setUser(null);
      setLoading(false);
    });
  }, [loadData]);

  const handleSaved = useCallback((savedUser: SystemUser) => {
    setShowEditModal(false);
    setUser(savedUser);
    loadData().catch((err) => {
      console.warn('[SystemUserDetailPage] Failed to reload data after save:', err);
    });
  }, [loadData]);

  const handleStatusChange = useCallback(async () => {
    if (!user || !statusChangeTarget) return;
    try {
      const updatedUser = await updateSystemUserStatus(user, statusChangeTarget);
      setUser(updatedUser as SystemUser);
      setStatusChangeTarget(null);
      loadData().catch((err) => {
        console.warn('[SystemUserDetailPage] Failed to reload data after status change:', err);
      });
    } catch {
      setStatusChangeTarget(null);
    }
  }, [loadData, statusChangeTarget, user]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading system user...</div>;
  }

  if (!user) {
    return <div className="p-8 text-center text-gray-500">System user not found</div>;
  }

  const statusCfg = STATUS_CONFIG[user.status] || { label: user.status, bg: 'bg-gray-100', text: 'text-gray-600' };
  const statusLabel = statusChangeTarget === 'deleted' ? 'soft-delete' : statusChangeTarget === 'suspended' ? 'suspend' : statusChangeTarget === 'active' ? 'reactivate' : '';
  const statusVariant = statusChangeTarget === 'deleted' ? 'danger' as const : statusChangeTarget === 'suspended' ? 'warning' as const : 'default' as const;

  return (
    <div className="p-8 max-w-6xl space-y-6">
      <button onClick={() => navigate('/users')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="h-4 w-4" /> Back to System Users
      </button>

      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4 min-w-0">
            <div className="shrink-0 h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center text-base font-bold text-gray-500">
              {(user.firstName?.[0] || '').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">{user.firstName} {user.lastName}</h1>
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <Mail className="h-4 w-4" /> {user.email}
              </p>
              {(user.title || user.position) && (
                <p className="text-sm text-gray-400 mt-1">{[user.title, user.position].filter(Boolean).join(' · ')}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${statusCfg.bg} ${statusCfg.text}`}>
              {statusCfg.label}
            </span>
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Pencil className="h-4 w-4" /> Edit
            </button>
            {user.status === 'active' && (
              <button
                onClick={() => setStatusChangeTarget('suspended')}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <UserX className="h-4 w-4" /> Suspend
              </button>
            )}
            {user.status === 'suspended' && (
              <button
                onClick={() => setStatusChangeTarget('active')}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <UserCheck className="h-4 w-4" /> Reactivate
              </button>
            )}
            {user.status !== 'deleted' && (
              <button
                onClick={() => setStatusChangeTarget('deleted')}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 className="h-4 w-4" /> Soft Delete
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <section className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-4 w-4 text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailItem label="First Name" value={user.firstName || '—'} />
            <DetailItem label="Last Name" value={user.lastName || '—'} />
            <DetailItem label="Email" value={user.email || '—'} />
            <DetailItem label="Status" value={statusCfg.label} />
            <DetailItem label="Title" value={user.title || '—'} />
            <DetailItem label="Position" value={user.position || '—'} />
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-4 w-4 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">System Role</h2>
          </div>
          <div className="space-y-4">
            <DetailItem label="Assigned Role" value={user.systemRole?.roleName || 'No system role assigned'} />
            <DetailItem label="Role ID" value={user.systemRole?.roleId || '—'} mono />
          </div>
        </section>
      </div>

      <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Details</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <DetailItem label="Created" value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'} />
          <DetailItem label="Last Login" value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'} />
          <DetailItem label="Last Updated" value={user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '—'} />
          <DetailItem label="Entra ID" value={user.entraUserId || '—'} mono />
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Login History</h2>
        </div>
        <LoginHistoryTab
          userId={user._id}
          active
          fetchFn={async (userId, opts?): Promise<LoginHistoryResult> => {
            const params: Record<string, string> = {};
            if (opts?.limit) params.limit = String(opts.limit);
            if (opts?.skip) params.skip = String(opts.skip);
            if (opts?.event) params.event = opts.event;
            const res = await getSystemUserLoginHistory(userId, params);
            return { data: res.data ?? [], total: res.total ?? 0, limit: opts?.limit ?? 20, skip: opts?.skip ?? 0 };
          }}
        />
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Audit Log</h2>
        </div>
        <AuditLogTimeline
          documentId={user._id}
          collection="users"
          theme={theme}
          active
          fetchFn={fetchAuditLogs}
        />
      </section>

      {showEditModal && (
        <EditSystemUserModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSaved={handleSaved}
        />
      )}

      {statusChangeTarget && (
        <ConfirmActionModal
          title={`${statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)} User?`}
          description={<>Are you sure you want to <strong>{statusLabel}</strong> <strong>{user.firstName} {user.lastName}</strong>?</>}
          confirmButtonLabel={`Yes, ${statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}`}
          variant={statusVariant}
          onConfirm={handleStatusChange}
          onClose={() => setStatusChangeTarget(null)}
        />
      )}
    </div>
  );
}

function DetailItem({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`mt-1 text-sm text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>{value}</p>
    </div>
  );
}
